import React, { useMemo } from "react";
import CommentIcon from "@mui/icons-material/Comment";
import Swal from "sweetalert2";

// Arma los ejes (semanas/vendedores/líneas) y el mapa de valores a partir del arreglo plano de
// GET /ReporteCotizaciones. Los ejes salen de los datos: si una semana/vendedor/línea no tiene
// ningún registro, no aparece. El objetivo mensual es el mismo en todas las filas de un
// vendedor (no varía por línea/semana), se toma una sola vez. Los vendedores quedan ordenados
// por idestado y luego idsucursal (ascendente), y alfabético por vendedor dentro de la misma
// sucursal, para poder agruparlos en el render con un miniheader de estado y otro de sucursal.
// Las líneas quedan en el orden en que aparecen en los datos (mismo criterio que
// ObjetivosSemanalesTabla): son un eje global, iguales para todas las semanas/vendedores.
const armarPivot = (datos) => {
  const semanasMap = new Map();
  const vendedoresMap = new Map();
  const lineasMap = new Map();
  const valores = {};

  datos.forEach((row) => {
    if (!semanasMap.has(row.idsemana)) {
      semanasMap.set(row.idsemana, { idsemana: row.idsemana, semana: row.semana, fecha_inicio: row.fecha_inicio });
    }
    if (!vendedoresMap.has(row.idvendedor)) {
      vendedoresMap.set(row.idvendedor, {
        idvendedor: row.idvendedor,
        vendedor: row.vendedor,
        idestado: row.idestado,
        estado: row.estado,
        idsucursal: row.idsucursal,
        sucursal: row.sucursal,
        objetivoMensual: row.objetivo_mensual,
      });
    }
    if (!lineasMap.has(row.idlinea)) lineasMap.set(row.idlinea, row.linea);
    valores[`${row.idsemana}_${row.idvendedor}_${row.idlinea}`] = {
      objetivo: row.objetivo,
      real: row.real,
      cumplimiento: row.cumplimiento_vp,
      tieneComentario: !!row.tiene_comentario,
      comentario: row.comentario,
    };
  });

  const semanas = [...semanasMap.values()].sort((a, b) => a.idsemana - b.idsemana);

  const vendedores = [...vendedoresMap.values()].sort((a, b) => {
    if (a.idestado !== b.idestado) return a.idestado - b.idestado;
    if (a.idsucursal !== b.idsucursal) return a.idsucursal - b.idsucursal;
    return (a.vendedor || "").localeCompare(b.vendedor || "", "es");
  });

  const lineas = [...lineasMap.entries()].map(([idlinea, linea]) => ({ idlinea, linea }));

  return { semanas, vendedores, lineas, valores };
};

// Agrupa vendedores consecutivos (ya vienen ordenados por idestado/idsucursal) en estados, y
// dentro de cada estado en sucursales, para pintar los dos miniheaders (banda de estado y banda
// de sucursal) antes de cada grupo, en vez de repetir esas columnas en cada renglón.
const agruparPorEstadoYSucursal = (vendedores) => {
  const estados = [];
  vendedores.forEach((v) => {
    let estadoActual = estados[estados.length - 1];
    if (!estadoActual || estadoActual.idestado !== v.idestado) {
      estadoActual = { idestado: v.idestado, estado: v.estado, sucursales: [] };
      estados.push(estadoActual);
    }
    let sucursalActual = estadoActual.sucursales[estadoActual.sucursales.length - 1];
    if (!sucursalActual || sucursalActual.idsucursal !== v.idsucursal) {
      sucursalActual = { idsucursal: v.idsucursal, sucursal: v.sucursal, vendedores: [] };
      estadoActual.sucursales.push(sucursalActual);
    }
    sucursalActual.vendedores.push(v);
  });
  return estados;
};

// Semáforo de cumplimiento: verde ≥100%, amarillo >80%, rojo ≤80%, gris "N/A" sin objetivo
// capturado esa semana/línea (cumplimiento_vp llega null desde el backend en ese caso).
const claseCumplimiento = (valor) => {
  if (valor === null || valor === undefined) return "ind-cot-cumpl-na";
  if (valor >= 100) return "ind-cot-cumpl-ok";
  if (valor > 80) return "ind-cot-cumpl-medio";
  return "ind-cot-cumpl-bajo";
};

// Totales del mes por vendedor: suma de "real" de todas las semanas y líneas del pivot,
// faltantes para llegar al objetivo mensual (nunca negativo) y el % acumulado — este último se
// calcula en frontend (a diferencia de cumplimiento_vp semanal, que manda el backend) porque no
// hay un endpoint que regrese el acumulado del mes ya resuelto.
const totalesVendedor = (vendedor, semanas, lineas, valores) => {
  let totalReal = 0;
  semanas.forEach((s) => {
    lineas.forEach((l) => {
      totalReal += valores[`${s.idsemana}_${vendedor.idvendedor}_${l.idlinea}`]?.real ?? 0;
    });
  });
  const objetivoMensual = Number(vendedor.objetivoMensual) || 0;
  if (objetivoMensual <= 0) return { totalReal, faltantes: null, porcentaje: null };
  const faltantes = Math.max(0, objetivoMensual - totalReal);
  const porcentaje = Math.round((totalReal / objetivoMensual) * 100);
  return { totalReal, faltantes, porcentaje };
};

// Muestra el comentario capturado por el backend para una celda objetivo/línea/semana (viene de
// "tiene_comentario"/"comentario" en cada row de GET /ReporteCotizaciones).
const onVerComentario = (comentario) => {
  Swal.fire({
    title: "Comentario",
    text: comentario || "Sin comentario",
    icon: "info",
    confirmButtonText: "Cerrar",
  });
};

/**
 * IndicadoresCotizacionesTabla
 * Reporte de solo lectura: pivotea el arreglo plano de GET /ReporteCotizaciones en semanas ×
 * líneas × vendedores. Mismo diseño que IndicadoresVisitasTabla (header obligatorio con imagen,
 * columnas Asesor/Objetivo mensual fijas, bandas de estado/sucursal, semáforo de cumplimiento y
 * columna de Total al final), con una variante: cada semana se divide además por línea de
 * negocio (Maquinaria Nueva, Maquinaria Usada, etc.), agregando un tercer nivel de encabezado
 * entre la semana y las subcolumnas Objetivo/Real/%. Las líneas son un eje global (salen de los
 * datos, no se inventan combinaciones). No llama a la API directamente: recibe los datos ya
 * cargados desde IndicadoresCotizacionesMainScreen. Cuando una celda de Objetivo trae
 * "tiene_comentario" true, se muestra un ícono de comentario a un lado del número; al hacer clic
 * despliega un modal (`Swal.fire`) de solo lectura con el texto de "comentario".
 *
 * Props:
 *   datos    — array plano de GET /ReporteCotizaciones (puede venir vacío); cada row puede traer
 *              también `tiene_comentario` (bool) y `comentario` (string) para esa
 *              semana/vendedor/línea
 *   cargando — bool, evita mostrar "Sin información" mientras carga
 */
export const IndicadoresCotizacionesTabla = ({ datos, cargando }) => {
  const pivot = useMemo(() => armarPivot(Array.isArray(datos) ? datos : []), [datos]);
  const gruposEstado = useMemo(() => agruparPorEstadoYSucursal(pivot.vendedores), [pivot]);

  if (cargando) return <p className="ind-cot-sin-datos">Cargando indicadores...</p>;
  if (pivot.semanas.length === 0 || pivot.lineas.length === 0) {
    return <p className="ind-cot-sin-datos">Sin información para el periodo seleccionado.</p>;
  }

  const totalColumnas = 2 + pivot.semanas.length * pivot.lineas.length * 3 + 3;

  return (
    <div className="ind-cot-card">
      {/* Aviso de scroll horizontal, visible solo en mobile (ver _CRM_IndicadoresCotizaciones.scss) */}
      <div className="ind-cot-scroll-hint">
        <span aria-hidden="true">&#8596;</span> Desliza para ver más semanas
      </div>

      <div className="ind-cot-tabla-wrap scroll">
        <table className="ind-cot-tabla">
          <colgroup>
            <col className="ind-cot-col-asesor" />
            <col className="ind-cot-col-meta" />
            {pivot.semanas.map((s) => (
              <React.Fragment key={s.idsemana}>
                {pivot.lineas.map((l) => (
                  <React.Fragment key={l.idlinea}>
                    <col className="ind-cot-col-num" />
                    <col className="ind-cot-col-num" />
                    <col className="ind-cot-col-cumpl" />
                  </React.Fragment>
                ))}
              </React.Fragment>
            ))}
            <col className="ind-cot-col-num" />
            <col className="ind-cot-col-num" />
            <col className="ind-cot-col-cumpl" />
          </colgroup>
          <thead>
            <tr>
              <th rowSpan={3} className="ind-cot-th-asesor">Asesor</th>
              <th rowSpan={3} className="ind-cot-th-objetivo-mensual">Objetivo mensual</th>
              {pivot.semanas.map((s, i) => (
                <th key={s.idsemana} colSpan={pivot.lineas.length * 3} className="ind-cot-th-semana ind-cot-wk-start">
                  <span className="ind-cot-wk-tag">Semana {i + 1}</span>
                  <span className="ind-cot-wk-range">{s.semana}</span>
                </th>
              ))}
              <th rowSpan={2} colSpan={3} className="ind-cot-th-semana ind-cot-wk-start">
                <span className="ind-cot-wk-tag">Total</span>
                <span className="ind-cot-wk-range">Del mes</span>
              </th>
            </tr>
            <tr>
              {pivot.semanas.map((s) => (
                <React.Fragment key={s.idsemana}>
                  {pivot.lineas.map((l, j) => (
                    <th
                      key={l.idlinea}
                      colSpan={3}
                      className={`ind-cot-th-linea${j === 0 ? " ind-cot-wk-start" : " ind-cot-ln-start"}`}
                    >
                      {l.linea}
                    </th>
                  ))}
                </React.Fragment>
              ))}
            </tr>
            <tr>
              {pivot.semanas.map((s) => (
                <React.Fragment key={s.idsemana}>
                  {pivot.lineas.map((l, j) => (
                    <React.Fragment key={l.idlinea}>
                      <th className={`ind-cot-th-sub${j === 0 ? " ind-cot-wk-start" : " ind-cot-ln-start"}`}>Objetivo</th>
                      <th className="ind-cot-th-sub">Real</th>
                      <th className="ind-cot-th-sub">%</th>
                    </React.Fragment>
                  ))}
                </React.Fragment>
              ))}
              <th className="ind-cot-th-sub ind-cot-wk-start">Real</th>
              <th className="ind-cot-th-sub">Faltan</th>
              <th className="ind-cot-th-sub">%</th>
            </tr>
          </thead>
          <tbody>
            {gruposEstado.map((grupoEstado) => (
              <React.Fragment key={grupoEstado.idestado}>
                <tr className="ind-cot-fila-estado">
                  <td colSpan={totalColumnas}>{grupoEstado.estado}</td>
                </tr>
                {grupoEstado.sucursales.map((grupoSucursal) => (
                  <React.Fragment key={grupoSucursal.idsucursal}>
                    <tr className="ind-cot-fila-sucursal">
                      <td colSpan={totalColumnas}>
                        <span className="ind-cot-dash" />{grupoSucursal.sucursal}
                      </td>
                    </tr>
                    {grupoSucursal.vendedores.map((v) => {
                      const { totalReal, faltantes, porcentaje } = totalesVendedor(v, pivot.semanas, pivot.lineas, pivot.valores);
                      return (
                        <tr key={v.idvendedor}>
                          <td className="ind-cot-td-vendedor">{v.vendedor}</td>
                          <td className="ind-cot-td-objetivo-mensual">{v.objetivoMensual}</td>
                          {pivot.semanas.map((s) => (
                            <React.Fragment key={s.idsemana}>
                              {pivot.lineas.map((l, j) => {
                                const valor = pivot.valores[`${s.idsemana}_${v.idvendedor}_${l.idlinea}`];
                                const objetivo = valor?.objetivo ?? 0;
                                const real = valor?.real ?? 0;
                                const cumplimiento = valor?.cumplimiento;
                                const clasePrimera = j === 0 ? " ind-cot-wk-start" : " ind-cot-ln-start";
                                return (
                                  <React.Fragment key={l.idlinea}>
                                    <td className={`ind-cot-td-num${clasePrimera}`}>
                                      {objetivo}
                                      {valor?.tieneComentario && (
                                        <CommentIcon
                                          className="ind-cot-icon-comentario"
                                          titleAccess="Ver comentario"
                                          onClick={() => onVerComentario(valor.comentario)}
                                        />
                                      )}
                                    </td>
                                    <td className="ind-cot-td-num">{real}</td>
                                    <td className={`ind-cot-td-cumpl ${claseCumplimiento(cumplimiento)}`}>
                                      {cumplimiento === null || cumplimiento === undefined ? "N/A" : `${Math.round(cumplimiento)}%`}
                                    </td>
                                  </React.Fragment>
                                );
                              })}
                            </React.Fragment>
                          ))}
                          <td className="ind-cot-td-num ind-cot-wk-start ind-cot-td-total">{totalReal}</td>
                          <td className="ind-cot-td-num ind-cot-td-total">{faltantes === null ? "—" : faltantes}</td>
                          <td className={`ind-cot-td-cumpl ind-cot-td-total ${claseCumplimiento(porcentaje)}`}>
                            {porcentaje === null ? "N/A" : `${porcentaje}%`}
                          </td>
                        </tr>
                      );
                    })}
                  </React.Fragment>
                ))}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};
